import { useState, type FormEvent } from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { CODIGOS_ERRO, ROTAS } from '@remoto/shared';
import { rotaRaiz } from './raiz';
import { chamar, ErroApi, type SessaoRecebida } from '../api';
import { usarSessao } from '../estado/sessao';
import { Aviso, Botao, Campo, Cartao } from '../componentes/campos';
import { mensagemDoErro } from '../erros';

type Etapa =
  | { nome: 'senha' }
  | { nome: 'codigo'; desafioTotp: string }
  | { nome: 'ativar'; desafioTotp: string; qr: string; segredoFormatado: string }
  | { nome: 'recuperacao'; desafioTotp: string }
  | { nome: 'esquecer' }
  | { nome: 'esquecer-enviado' }
  | { nome: 'codigos'; codigos: string[] };

function Entrar(): React.JSX.Element {
  const navegar = useNavigate();
  const entrarNaSessao = usarSessao((e) => e.entrar);
  const motivoSaida = usarSessao((e) => e.motivoSaida);

  const [etapa, setEtapa] = useState<Etapa>({ nome: 'senha' });
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function executar(acao: () => Promise<void>): Promise<void> {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      /*
        Desafio expirado não é um erro para exibir no lugar onde se está: a
        etapa inteira morreu, e o QR (ou o campo de código) que está na tela
        pertence a ela. Ficar ali só rende tentativas que vão falhar todas.
        Voltamos para a senha, que é de onde um desafio novo nasce.
      */
      if (e instanceof ErroApi && e.codigo === CODIGOS_ERRO.desafioExpirado) {
        setCodigo('');
        setEtapa({ nome: 'senha' });
      }
      setErro(mensagemDoErro(e, 'Não foi possível completar a operação.'));
    } finally {
      setOcupado(false);
    }
  }

  function abrirSessao(sessao: SessaoRecebida): void {
    entrarNaSessao(sessao.acesso, sessao.expiraEm, sessao.usuario);
    // Os códigos de recuperação aparecem uma única vez, logo após a ativação.
    if (sessao.codigosRecuperacao?.length) {
      setEtapa({ nome: 'codigos', codigos: sessao.codigosRecuperacao });
      return;
    }
    void navegar({ to: '/' });
  }

  const enviarSenha = (evento: FormEvent) => {
    evento.preventDefault();
    void executar(async () => {
      const resposta = await chamar<
        Partial<SessaoRecebida> & { desafioTotp?: string; precisaAtivarTotp?: boolean }
      >(ROTAS.auth.login, { method: 'POST', corpo: { email, senha }, semRenovar: true });
      setSenha('');

      // Com o segundo fator desligado no servidor, o login ja devolve a sessao.
      if (resposta.acesso) {
        abrirSessao(resposta as SessaoRecebida);
        return;
      }
      if (!resposta.desafioTotp) throw new Error('resposta inesperada do servidor');

      if (!resposta.precisaAtivarTotp) {
        setEtapa({ nome: 'codigo', desafioTotp: resposta.desafioTotp });
        return;
      }
      const desafioTotp = resposta.desafioTotp;
      const ativacao = await chamar<{ otpauth: string; segredoFormatado: string }>(
        ROTAS.auth.totpAtivar,
        { method: 'POST', corpo: { desafioTotp }, semRenovar: true },
      );
      setEtapa({
        nome: 'ativar',
        desafioTotp,
        qr: await QRCode.toDataURL(ativacao.otpauth, { margin: 1, width: 220 }),
        segredoFormatado: ativacao.segredoFormatado,
      });
    });
  };

  const enviarCodigo = (evento: FormEvent) => {
    evento.preventDefault();
    if (etapa.nome !== 'codigo' && etapa.nome !== 'ativar') return;
    const desafioTotp = etapa.desafioTotp;
    const caminho = etapa.nome === 'ativar' ? ROTAS.auth.totpConfirmar : ROTAS.auth.totp;
    void executar(async () => {
      const sessao = await chamar<SessaoRecebida>(caminho, {
        method: 'POST',
        corpo: { desafioTotp, codigo },
        semRenovar: true,
      });
      setCodigo('');
      abrirSessao(sessao);
    });
  };

  const enviarEsquecer = (evento: FormEvent) => {
    evento.preventDefault();
    void executar(async () => {
      await chamar<{ ok: boolean }>(ROTAS.auth.senhaEsquecer, {
        method: 'POST',
        corpo: { email },
        semRenovar: true,
      });
      setEtapa({ nome: 'esquecer-enviado' });
    });
  };

  const enviarRecuperacao = (evento: FormEvent) => {
    evento.preventDefault();
    if (etapa.nome !== 'recuperacao') return;
    const desafioTotp = etapa.desafioTotp;
    void executar(async () => {
      const sessao = await chamar<SessaoRecebida>(ROTAS.auth.recuperacao, {
        method: 'POST',
        corpo: { desafioTotp, codigo },
        semRenovar: true,
      });
      setCodigo('');
      abrirSessao(sessao);
    });
  };

  if (etapa.nome === 'senha') {
    return (
      <Cartao titulo="Remoto" descricao="Entre para acompanhar suas sessões.">
        <form onSubmit={enviarSenha} className="space-y-4">
          {motivoSaida === 'substituida' ? (
            <Aviso>Sua conta entrou em outro aparelho. Este perfil permite um acesso por vez.</Aviso>
          ) : null}
          <Campo
            rotulo="E-mail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <Campo
            rotulo="Senha"
            type="password"
            autoComplete="current-password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
          />
          {erro ? <Aviso>{erro}</Aviso> : null}
          <Botao carregando={ocupado}>Entrar</Botao>
          <button
            type="button"
            onClick={() => {
              setErro(null);
              setEtapa({ nome: 'esquecer' });
            }}
            className="w-full text-center text-xs text-zinc-500 underline-offset-2 hover:underline"
          >
            Esqueci a senha
          </button>
        </form>
      </Cartao>
    );
  }

  if (etapa.nome === 'esquecer') {
    return (
      <Cartao
        titulo="Esqueci a senha"
        descricao="Diga o e-mail da conta e enviaremos um link para escolher uma senha nova."
      >
        <form onSubmit={enviarEsquecer} className="space-y-4">
          <Campo
            rotulo="E-mail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          {erro ? <Aviso>{erro}</Aviso> : null}
          <Botao carregando={ocupado}>Enviar o link</Botao>
          <button
            type="button"
            onClick={() => {
              setErro(null);
              setEtapa({ nome: 'senha' });
            }}
            className="w-full text-center text-xs text-zinc-500 underline-offset-2 hover:underline"
          >
            Voltar para a entrada
          </button>
        </form>
      </Cartao>
    );
  }

  if (etapa.nome === 'esquecer-enviado') {
    return (
      <Cartao titulo="Confira sua caixa de entrada">
        <div className="space-y-4">
          {/*
            A frase é NEUTRA de propósito: a anti-enumeração vale também na
            tela. Confirmar "enviado" só para conta existente entregaria a
            lista de e-mails válidos a quem tentar adivinhar.
          */}
          <p className="text-sm text-zinc-400">
            Se existe uma conta com este e-mail, o link de redefinição acabou de ser enviado. Ele
            vale por 30 minutos.
          </p>
          <Botao type="button" onClick={() => setEtapa({ nome: 'senha' })}>
            Voltar para a entrada
          </Botao>
        </div>
      </Cartao>
    );
  }

  if (etapa.nome === 'ativar') {
    return (
      <Cartao
        titulo="Ative a verificação em duas etapas"
        descricao="Leia o código no aplicativo autenticador e digite os seis dígitos que ele mostrar."
      >
        <div className="space-y-4">
          <img
            src={etapa.qr}
            alt="Código para o aplicativo autenticador"
            className="mx-auto rounded-md bg-white p-2"
            width={220}
            height={220}
          />
          <p className="text-center text-xs text-zinc-500">
            Sem câmera? Digite esta chave no aplicativo:
            <br />
            <span className="font-mono text-zinc-300">{etapa.segredoFormatado}</span>
          </p>
          <form onSubmit={enviarCodigo} className="space-y-4">
            <Campo
              rotulo="Código de seis dígitos"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
            {erro ? <Aviso>{erro}</Aviso> : null}
            <Botao carregando={ocupado}>Confirmar</Botao>
          </form>
        </div>
      </Cartao>
    );
  }

  if (etapa.nome === 'codigo') {
    return (
      <Cartao titulo="Verificação em duas etapas" descricao="Digite o código do aplicativo.">
        <form onSubmit={enviarCodigo} className="space-y-4">
          <Campo
            rotulo="Código de seis dígitos"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
            required
            autoFocus
          />
          {erro ? <Aviso>{erro}</Aviso> : null}
          <Botao carregando={ocupado}>Entrar</Botao>
          <button
            type="button"
            onClick={() => {
              setCodigo('');
              setErro(null);
              setEtapa({ nome: 'recuperacao', desafioTotp: etapa.desafioTotp });
            }}
            className="w-full text-center text-xs text-zinc-500 underline-offset-2 hover:underline"
          >
            Perdi o acesso ao aplicativo
          </button>
        </form>
      </Cartao>
    );
  }

  if (etapa.nome === 'recuperacao') {
    return (
      <Cartao
        titulo="Código de recuperação"
        descricao="Use um dos códigos que você guardou ao ativar a verificação. Cada um serve uma vez."
      >
        <form onSubmit={enviarRecuperacao} className="space-y-4">
          <Campo
            rotulo="Código de recuperação"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX-XXXX"
            required
            autoFocus
          />
          {erro ? <Aviso>{erro}</Aviso> : null}
          <Botao carregando={ocupado}>Entrar</Botao>
        </form>
      </Cartao>
    );
  }

  return (
    <Cartao
      titulo="Guarde estes códigos"
      descricao="Eles aparecem uma única vez e servem para entrar se você perder o celular."
    >
      <div className="space-y-4">
        <ul className="grid grid-cols-2 gap-2 font-mono text-sm text-zinc-200">
          {etapa.codigos.map((c) => (
            <li key={c} className="rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-center">
              {c}
            </li>
          ))}
        </ul>
        <Botao type="button" onClick={() => void navegar({ to: '/' })}>
          Guardei, continuar
        </Botao>
      </div>
    </Cartao>
  );
}

export const rotaEntrar = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/entrar',
  component: Entrar,
});
